import React, { useCallback, useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Copy, Mail, RefreshCw, Loader2, Download } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function formatDateAR(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

export default function LeadsAndTrialsSection({ adminSecret, token }) {
  const [days, setDays] = useState(30);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null); // email currently being resent
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    if (!adminSecret || !token) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/api/admin/leads-and-trials?days=${days}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Admin-Secret': adminSecret,
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setItems(data.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [adminSecret, token, days]);

  useEffect(() => { load(); }, [load]);

  const handleCopy = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success(`${email} copiado`);
    } catch {
      toast.error('No se pudo copiar');
    }
  };

  const handleResend = async (email) => {
    if (!window.confirm(`¿Reenviar acceso a ${email}?`)) return;
    setSendingEmail(email);
    try {
      const res = await fetch(`${API}/api/admin/resend-access`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'X-Admin-Secret': adminSecret,
        },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || data.message || `HTTP ${res.status}`);
      if (data.email_sent) {
        toast.success(`Acceso enviado a ${email}`);
      } else {
        toast.warning('Resend no configurado — revisá logs');
      }
      load();
    } catch (e) {
      toast.error(e.message || 'Error al reenviar acceso');
    } finally {
      setSendingEmail(null);
    }
  };

  const handleExportEmails = async (fmt = 'txt') => {
    if (!adminSecret || !token) return;
    setExporting(true);
    try {
      const res = await fetch(`${API}/api/admin/leads/export-emails?fmt=${fmt}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Admin-Secret': adminSecret,
        },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const count = parseInt(res.headers.get('x-lead-email-count') || '0', 10);
      const text = await res.text();
      // Copy to clipboard for one-click paste
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        // ignore clipboard failure — file download still works
      }
      // Trigger file download
      const blob = new Blob([text], {
        type: fmt === 'csv' ? 'text/csv;charset=utf-8' : 'text/plain;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fmt === 'csv' ? 'lead_emails.csv' : 'lead_emails.txt';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`${count} emails exportados · copiados al portapapeles`);
    } catch (e) {
      toast.error(e.message || 'No se pudieron exportar los emails');
    } finally {
      setExporting(false);
    }
  };

  const filters = [
    { label: '1 día', value: 1 },
    { label: '7 días', value: 7 },
    { label: '30 días', value: 30 },
  ];

  return (
    <section className="mb-8" data-testid="leads-trials-section">
      <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
        <h2 className="text-xs uppercase tracking-wider text-purple-400 font-semibold">
          Leads y Trials
        </h2>
        <div className="flex items-center gap-2">
          {filters.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setDays(f.value)}
              data-testid={`leads-filter-${f.value}d`}
              className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                days === f.value
                  ? 'bg-purple-500/20 border-purple-400 text-purple-200'
                  : 'bg-white/5 border-white/10 text-slate-400 hover:text-white hover:border-white/20'
              }`}
            >
              {f.label}
            </button>
          ))}
          <button
            type="button"
            onClick={() => handleExportEmails('txt')}
            disabled={exporting}
            data-testid="leads-export-emails"
            title="Exportar TODOS los emails del checklist (histórico completo, sin duplicados). Descarga .txt y copia al portapapeles."
            className="inline-flex items-center gap-1.5 px-3 py-1 text-xs rounded-full border transition-colors bg-[#5B9B2F]/15 border-[#5B9B2F]/40 text-[#5B9B2F] hover:bg-[#5B9B2F]/25 disabled:opacity-50"
          >
            {exporting ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Download className="h-3 w-3" />
            )}
            Exportar emails
          </button>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            aria-label="Refrescar"
            data-testid="leads-refresh"
            className="p-1.5 text-slate-400 hover:text-white rounded-md disabled:opacity-50"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="leads-trials-table">
            <thead className="bg-white/[0.03] text-[11px] uppercase tracking-wider text-slate-400">
              <tr>
                <th className="text-left px-4 py-3 font-semibold">Email</th>
                <th className="text-left px-4 py-3 font-semibold">Tipo</th>
                <th className="text-left px-4 py-3 font-semibold">Creado</th>
                <th className="text-center px-4 py-3 font-semibold">Descargas</th>
                <th className="text-center px-4 py-3 font-semibold">Estado</th>
                <th className="text-right px-4 py-3 font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-slate-500">Cargando…</td></tr>
              )}
              {error && (
                <tr><td colSpan={6} className="text-center py-8 text-red-400">Error: {error}</td></tr>
              )}
              {!loading && !error && items.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-slate-500">Sin registros en el período.</td></tr>
              )}
              {items.map((it, idx) => (
                <tr
                  key={`${it.email}-${it.type}-${idx}`}
                  className="border-t border-white/5 hover:bg-white/[0.02] transition-colors"
                  data-testid={`leads-row-${idx}`}
                >
                  <td className="px-4 py-3 text-white font-mono text-xs sm:text-sm break-all">
                    {it.email}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold ${
                      it.type === 'trial'
                        ? 'bg-cyan-500/15 text-cyan-300 border border-cyan-500/30'
                        : it.type === 'demo'
                        ? 'bg-orange-500/15 text-orange-300 border border-orange-500/30'
                        : 'bg-purple-500/15 text-purple-300 border border-purple-500/30'
                    }`} data-testid={`leads-row-${idx}-type`}>
                      {it.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-xs whitespace-nowrap">
                    {formatDateAR(it.created_at)}
                  </td>
                  <td className="px-4 py-3 text-center text-slate-300 text-xs">
                    {it.download_count ?? '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {it.is_full ? (
                      <span className="inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold bg-[#5B9B2F]/15 text-[#5B9B2F] border border-[#5B9B2F]/30">
                        Full
                      </span>
                    ) : (
                      <span className="text-slate-500 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleCopy(it.email)}
                        data-testid={`leads-row-${idx}-copy`}
                        className="h-7 px-2 text-slate-300 hover:text-white hover:bg-white/10"
                      >
                        <Copy className="h-3 w-3 mr-1" /> Copiar
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handleResend(it.email)}
                        disabled={sendingEmail === it.email}
                        data-testid={`leads-row-${idx}-resend`}
                        className="h-7 px-2 bg-[#5B9B2F]/20 hover:bg-[#5B9B2F]/30 text-[#5B9B2F] border border-[#5B9B2F]/30 disabled:opacity-50"
                      >
                        {sendingEmail === it.email ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <><Mail className="h-3 w-3 mr-1" /> Reenviar acceso</>
                        )}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && items.length > 0 && (
          <div className="px-4 py-2 text-xs text-slate-500 border-t border-white/5">
            Mostrando {items.length} {items.length === 1 ? 'registro' : 'registros'} · Últimos {days} día{days !== 1 ? 's' : ''}
          </div>
        )}
      </div>
    </section>
  );
}
